import { CertAnswer, CertQuestionType } from '@unity-learning/shared';

/** Ce que la règle lit d'une question : son type et son contenu affiché. */
interface QuestionShape {
    type: string;
    content: unknown;
}

const holesOf = (codeTemplate: unknown): string[] =>
    [...String(codeTemplate ?? '').matchAll(/\[\[(\w+)\]\]/g)].map(m => m[1]);

const isFilled = (value: unknown): boolean =>
    typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');

/**
 * Une question compte comme « répondue » (récapitulatif, avertissement avant de terminer) seulement
 * quand toutes ses cases sont remplies : un glisser-déposer à moitié rempli reste à finir.
 */
export function isAnswerComplete(question: QuestionShape, response: CertAnswer | null | undefined): boolean {
    if (response === null || response === undefined) return false;
    if (typeof response === 'boolean') return true;
    if (typeof response === 'string') return response !== '';

    const content = (question.content ?? {}) as Record<string, any>;

    if (Array.isArray(response)) {
        const max = content.max_selection;
        return typeof max === 'number' ? response.length === max : response.length > 0;
    }

    if (typeof response !== 'object') return false;
    const answers = response as Record<string, unknown>;

    switch (question.type) {
        case CertQuestionType.TYPE_CODE_DROPDOWN:
        case CertQuestionType.TYPE_CODE_INPUT:
        case CertQuestionType.TYPE_CODE_CONSTRUCTION: {
            const holes = holesOf(content.code_template);
            return holes.length > 0 ? holes.every(id => isFilled(answers[id])) : Object.keys(answers).length > 0;
        }
        case CertQuestionType.TYPE_MATRIX: {
            const rows: { id: string }[] = content.rows ?? [];
            return rows.length > 0 ? rows.every(row => isFilled(answers[row.id])) : Object.keys(answers).length > 0;
        }
        case CertQuestionType.TYPE_MATCHING_DRAG: {
            const targets: { id: string }[] = content.targets ?? [];
            return targets.length > 0 ? targets.every(t => isFilled(answers[t.id])) : Object.keys(answers).length > 0;
        }
        default:
            return Object.keys(answers).length > 0;
    }
}
